import React from 'react';
import {
  SwipeableList,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  Type as ListType,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import { Check, AlertCircle, Undo2 } from 'lucide-react';

const CATEGORY_COLORS = {
  pipeline: 'bg-green-100 text-green-700',
  compliance: 'bg-blue-100 text-blue-700',
  discipline: 'bg-red-100 text-red-700',
  revenue: 'bg-purple-100 text-purple-700',
  operations: 'bg-orange-100 text-orange-700',
  coaching: 'bg-teal-100 text-teal-700',
  default: 'bg-slate-100 text-slate-600',
};

const TIME_SLOT_LABELS = {
  morning_peak: '6-10 AM',
  off_peak: '10 AM-4 PM',
  evening_peak: '4-10 PM',
};

/**
 * SwipeableTask - iOS-style swipe-to-complete task item.
 * Swipe right on pending/overdue = mark complete (green reveal).
 * Swipe right on completed = uncomplete/undo (orange reveal).
 */
export default function SwipeableTask({ task, onComplete, onUncomplete }) {
  const isCompleted = task.status === 'completed';
  const isOverdue = task.status === 'overdue';
  const categoryColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.default;

  const trailingActions = () => (
    <TrailingActions>
      <SwipeAction
        onClick={() => {
          if (isCompleted) {
            onUncomplete(task.task_id);
          } else {
            onComplete(task.task_id);
          }
        }}
      >
        <div
          className={`flex items-center justify-center px-6 h-full ${
            isCompleted ? 'bg-orange-500' : 'bg-green-500'
          } text-white font-semibold text-sm`}
        >
          {isCompleted ? (
            <>
              <Undo2 className="w-5 h-5 mr-1" />
              Undo
            </>
          ) : (
            <>
              <Check className="w-5 h-5 mr-1" />
              Done
            </>
          )}
        </div>
      </SwipeAction>
    </TrailingActions>
  );

  return (
    <SwipeableList type={ListType.IOS} fullSwipe={false}>
      <SwipeableListItem trailingActions={trailingActions()} threshold={0.25}>
        <div
          className={`w-full py-3 px-4 border-b border-slate-100 transition-colors ${
            isCompleted
              ? 'bg-green-50'
              : isOverdue
              ? 'bg-red-50'
              : 'bg-white'
          }`}
        >
          <div className="flex items-start gap-3">
            {/* Status indicator circle */}
            <div className="flex-shrink-0 mt-0.5">
              {isCompleted ? (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              ) : isOverdue ? (
                <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                  <AlertCircle className="w-3 h-3 text-white" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
              )}
            </div>

            {/* Task content */}
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium ${
                  isCompleted
                    ? 'line-through text-slate-400'
                    : 'text-slate-800'
                }`}
              >
                {task.task_name}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${categoryColor}`}
                >
                  {task.category}
                </span>
                {task.time_slot && TIME_SLOT_LABELS[task.time_slot] && (
                  <span className="text-xs text-slate-400">
                    {TIME_SLOT_LABELS[task.time_slot]}
                  </span>
                )}
              </div>
              {isOverdue && task.overdue_days > 0 && (
                <span className="inline-block mt-1 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                  Day {task.overdue_days} overdue
                </span>
              )}
            </div>
          </div>
        </div>
      </SwipeableListItem>
    </SwipeableList>
  );
}
